<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { useDefaultToast } from "@/composables/useDefaultToast";
import { ROUTES } from '@/constants/routes';
import { toCalendarDayUtcIso } from "@/helpers/date";
import workerService from "@/modules/worker/services/workerService";
import { useWorkerAddIdentificationStore } from "@/modules/worker/store/workerAddIdentificationStore";
import { useRegistrationsStore } from "@/store/registrationsStore";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Modal from "@/dsComponents/modals/Modal.vue";

import IdentificationWorkerCard from "@/modules/worker/components/create/IdentificationWorkerCard.vue";

interface DuplicatedWorkerModalProps {
    open: boolean;
    companyNeedId?: string;
}

const props = defineProps<DuplicatedWorkerModalProps>();

const emit = defineEmits<{
    close: [];
}>();

const { t } = useI18n();
const router = useRouter();
const { toastError } = useDefaultToast();
const workerAddIdentificationStore = useWorkerAddIdentificationStore();
const registrationsStore = useRegistrationsStore();
const isLoading = ref(false);

const hasOneDuplicatedWorker = computed(() => {
    return workerAddIdentificationStore.duplicatedWorkers.length === 1;
});

const modalTitle = computed(() => {
    return hasOneDuplicatedWorker.value
        ? t("page.worker.modal.create.title.oneIdenticateProfil")
        : t("page.worker.modal.create.title.severalIdenticateProfils");
});

const createNewCandidate = async () => {
    try {
        isLoading.value = true;

        const { findWorkerForm, cv } = workerAddIdentificationStore;
        const payload = {
            firstname: findWorkerForm.firstname.value,
            lastname: findWorkerForm.lastname.value,
            email: findWorkerForm.email.value,
            phone: findWorkerForm.phone.value,
            birthDate: toCalendarDayUtcIso(findWorkerForm.birthDate.value),
            cv: { path: cv },
            source: findWorkerForm.origin.value?.value,
            jobs: findWorkerForm.jobs.value?.map((job) => job.value) ?? [],
            besoinId: props.companyNeedId,
            addToRegistrationsTracking: workerAddIdentificationStore.addToRegistrationsTracking,
        };

        const result = await workerService.createWorker(payload, {
            jobsFromForm: findWorkerForm.jobs.value,
        });

        if (payload.addToRegistrationsTracking) registrationsStore.incrementCount();

        router.push({ name: ROUTES.APP.WORKERS.PROFIL._ROOT, params: { workerId: result.id } });

        handleClose();
    } catch (error: unknown) {
        const data = (error as { response?: { data?: { code?: string; message?: string } } }).response?.data;
        const errorMessage =
            data?.code === "WORKER.DUPLICATE_EMAIL"
                ? t("page.worker.modal.create.errors.email")
                : data?.message || t("page.worker.modal.create.errors.default");
        toastError(errorMessage);
    } finally {
        isLoading.value = false;
    }
};

const handleViewDuplicatedWorkerProfil = () => {
    const duplicatedWorker = workerAddIdentificationStore.duplicatedWorkers[0];
    router.push({ name: ROUTES.APP.WORKERS.PROFIL._ROOT, params: { workerId: duplicatedWorker.id } });
    handleClose();
};

const handleClose = () => {
    workerAddIdentificationStore.reset();
    emit("close");
};
</script>
<template>
    <Modal class="duplicated-worker-modal" :title="modalTitle" width="500px" :open="open" close @close="handleClose">
        <div v-if="hasOneDuplicatedWorker" class="duplicated-worker-modal__identification">
            <IdentificationWorkerCard :worker="workerAddIdentificationStore.duplicatedWorkers[0]" />
        </div>

        <div v-else class="duplicated-worker-modal__identification duplicated-worker-modal__identification--multiple">
            <IdentificationWorkerCard
                v-for="duplicatedWorker of workerAddIdentificationStore.duplicatedWorkers"
                :key="`identification-worker-card__${duplicatedWorker.id}`"
                :worker="duplicatedWorker"
                display-action-button
            />
        </div>

        <template #footer>
            <div class="duplicated-worker-modal__footer-buttons">
                <div class="duplicated-worker-modal__footer-buttons-left">
                    <ButtonAlt type="neutral" :label="t('global.actions.cancel')" @click="handleClose" />
                </div>

                <div class="duplicated-worker-modal__footer-buttons-right">
                    <ButtonAlt
                        variant="stroke"
                        type="neutral"
                        :label="t('page.worker.modal.create.createNewProfile')"
                        :loading="isLoading"
                        @click="createNewCandidate"
                    />
                    <ButtonAlt
                        v-if="hasOneDuplicatedWorker"
                        variant="stroke"
                        type="neutral"
                        :label="t('global.actions.viewProfile')"
                        :loading="isLoading"
                        @click="handleViewDuplicatedWorkerProfil"
                    />
                </div>
            </div>
        </template>
    </Modal>
</template>

<style scoped lang="scss">
.duplicated-worker-modal {
    &__identification {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xl, 16px);

        &--multiple {
            .identification-worker-card {
                padding: var(--spacing-xl, 16px);
                border-radius: var(--radius-md, 8px);
                border: 1px solid var(--stroke-soft-200, #dbdfe6);
            }
        }
    }

    &__footer {
        &-buttons {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: var(--spacing-lg, 12px);

            &-left,
            &-right {
                display: flex;
                align-items: center;
                gap: var(--spacing-lg, 12px);
            }
        }
    }
}
</style>
